/**
 * Pure calculation utilities for the Spray Dashboard.
 * No React, no UI concerns — only data in, data out.
 */
import moment from 'moment';
import type { CoatingRow } from './PumpHouseCoatingTable';

/** Strict-parse coating sheet dates (cell formats from CSV / API). */
const COATING_DATE_FORMATS = ['DD.MM.YYYY', 'DD/MM/YYYY', 'DD-MM-YYYY', 'YYYY-MM-DD'] as const;

/** Date + wall time; `H:mm:ss` covers API values like `9:59:35`. */
const COATING_DATETIME_FORMATS = [
  'DD.MM.YYYY HH:mm:ss',
  'DD.MM.YYYY H:mm:ss',
  'DD/MM/YYYY HH:mm:ss',
  'DD/MM/YYYY H:mm:ss',
  'DD-MM-YYYY HH:mm:ss',
  'DD-MM-YYYY H:mm:ss',
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DD H:mm:ss',
] as const;

function parseCoatingDateOnly(raw: string): moment.Moment {
  const d = (raw ?? '').trim();
  if (!d) return moment.invalid();
  return moment(d, [...COATING_DATE_FORMATS], true);
}

function parseCoatingRowMoment(r: CoatingRow): moment.Moment {
  const ds = (r.date ?? '').trim();
  const ts = (r.time ?? '').trim();
  if (!ds) return moment.invalid();
  if (!ts) return parseCoatingDateOnly(ds);
  return moment(`${ds} ${ts}`, [...COATING_DATETIME_FORMATS], true);
}

function rowChronologicalSortValue(r: CoatingRow): number {
  const m = parseCoatingRowMoment(r);
  return m.isValid() ? m.valueOf() : Number.MAX_SAFE_INTEGER;
}

// ─── types ────────────────────────────────────────────────────────────────────

export interface TemperatureLimits {
  waterMin: number; waterMax: number;
  baseMin: number;  baseMax: number;
  hardenerMin: number; hardenerMax: number;
}

export const DEFAULT_LIMITS: TemperatureLimits = {
  waterMin: 66, waterMax: 75,
  baseMin: 74,  baseMax: 78,
  hardenerMin: 36, hardenerMax: 40,
};

// ─── return-type shape (for consumers) ───────────────────────────────────────

export interface DashKpis {
  /** Distinct normalized calendar day + joint after first joint 1 per session (retries same day/joint count once). */
  jointsSprayed: number;
  fpYield: number;
  /** Same buckets as fpYield; pass if any attempt in the bucket is a valid first-pass row. */
  totalYield: number;
  medianCycle: string;
  downtimeStr: string;
  gapCount: number;
  dewMargin: number;
}

export interface DailyJoint { date: string; pass: number; fail: number; }
export interface FaultEntry  { reason: string; count: number; color: string; }
export interface EnvEntry    { label: string; min: number; max: number; unit: string; }
export interface OperatorEntry { name: string; joints: number; yield: number; }

export interface MaterialInfo {
  coatingType: string;
  totalJoints: number;
  baseBatches: number;
  hardenerBatches: number;
  avgPerBatch: string;
}

export interface ExcursionCounts { total: number; water: number; base: number; hard: number; }

/** Non-event rows only — same idea as reference `jointRecords` on `CoatingRecord` (`!is_event`). */
export function jointRecords(records: CoatingRow[]): CoatingRow[] {
  return records.filter((r) => !r.isEvent);
}

/**
 * Counts temperature excursions on joint rows only.
 * If water temp is 0 (sensor dropout), the entire joint is skipped for all three sensors —
 * same rule as the reference `countExcursions` implementation (uses `jointRecords`).
 */
export function countExcursions(
  records: CoatingRow[],
  limits: TemperatureLimits,
): ExcursionCounts {
  let water = 0;
  let base = 0;
  let hard = 0;

  for (const r of jointRecords(records)) {
    const wt = parseInt(String(r.waterTemp), 10);
    const bt = parseInt(String(r.baseTemp), 10);
    const ht = parseInt(String(r.hardenerTemp), 10);
    if (wt === 0) continue;
    if (wt < limits.waterMin || wt > limits.waterMax) water++;
    if (bt < limits.baseMin || bt > limits.baseMax) base++;
    if (ht < limits.hardenerMin || ht > limits.hardenerMax) hard++;
  }

  return { total: water + base + hard, water, base, hard };
}

/** Low vs high excursion counts per sensor (joint rows with water temp 0 are skipped entirely). */
export interface ExcursionBreakdownDetail {
  waterLow: number;
  waterHigh: number;
  baseLow: number;
  baseHigh: number;
  hardLow: number;
  hardHigh: number;
}

export function countExcursionBreakdown(
  records: CoatingRow[],
  limits: TemperatureLimits,
): ExcursionBreakdownDetail {
  const z: ExcursionBreakdownDetail = {
    waterLow: 0, waterHigh: 0, baseLow: 0, baseHigh: 0, hardLow: 0, hardHigh: 0,
  };
  for (const r of jointRecords(records)) {
    const wt = parseInt(String(r.waterTemp), 10);
    const bt = parseInt(String(r.baseTemp), 10);
    const ht = parseInt(String(r.hardenerTemp), 10);
    if (wt === 0) continue;
    if (wt < limits.waterMin) z.waterLow++;
    else if (wt > limits.waterMax) z.waterHigh++;
    if (bt < limits.baseMin) z.baseLow++;
    else if (bt > limits.baseMax) z.baseHigh++;
    if (ht < limits.hardenerMin) z.hardLow++;
    else if (ht > limits.hardenerMax) z.hardHigh++;
  }
  return z;
}

/** Summary + detail strings for the Environmental window excursion strip. */
export function environmentalExcursionCopy(
  records: CoatingRow[],
  limits: TemperatureLimits,
): { summary: string; detail: string } | null {
  const joints = jointRecords(records).length;
  if (joints === 0) return null;
  const exc = countExcursions(records, limits);
  const pct = ((exc.total / joints) * 100).toFixed(1);
  const summary = `${exc.total} excursions across ${joints} joints (${pct}%)`;
  const b = countExcursionBreakdown(records, limits);
  const parts: string[] = [];
  if (b.waterLow)  parts.push(`Water low: ${b.waterLow}`);
  if (b.waterHigh) parts.push(`Water high: ${b.waterHigh}`);
  if (b.hardLow)   parts.push(`Hardener low: ${b.hardLow}`);
  if (b.hardHigh)  parts.push(`Hardener high: ${b.hardHigh}`);
  if (b.baseLow)   parts.push(`Base low: ${b.baseLow}`);
  if (b.baseHigh)  parts.push(`Base high: ${b.baseHigh}`);
  const detail = parts.length > 0 ? parts.join(' · ') : '';
  return { summary, detail };
}

export type SessionStatus = 'pass' | 'warn' | 'fail';

export interface SessionSummary {
  /** Composite key `"${date}||${user}"` — use to filter source rows. */
  key: string;
  date: string;
  user: string;
  coatingType: string;
  baseBatch: string;
  hardenerBatch: string;
  /** Distinct (normalized calendar day, joint) from first joint 1 onward — same joint on different days counts twice. */
  joints: number;
  /** Total spray attempts (includes retries). */
  attempts: number;
  fpYield: number;
  /**
   * Same denominator as {@link fpYield}: one bucket per (calendar day, numeric joint).
   * Pass if **any** attempt in that bucket passes {@link isFirstPass} and is not a water-temp sensor fault.
   */
  totalYield: number;
  medianCycle: string;
  faultCount: number;
  status: SessionStatus;
  /** Source file URL from any row in this session (date + user). */
  s3Link?: string;
}

export interface DashData {
  kpis: DashKpis;
  dailyJoints: DailyJoint[];
  faults: FaultEntry[];
  envData: EnvEntry[];
  operators: OperatorEntry[];
  material: MaterialInfo;
  excursions: ExcursionCounts;
}

// ─── constants ────────────────────────────────────────────────────────────────

const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const GAP_THRESHOLD_SECS = 300; // 5 minutes

/** Fault bar colours — ordered by frequency rank */
export const FAULT_COLORS = ['#EB1C37', '#C4001F', '#FFCA57', '#FFCA57', '#00273F'];
const FAULT_COLOR_FALLBACK = '#969696';

// ─── private helpers ──────────────────────────────────────────────────────────

function groupBy<T>(arr: T[], keyFn: (x: T) => string): Record<string, T[]> {
  return arr.reduce<Record<string, T[]>>((acc, x) => {
    const k = keyFn(x);
    (acc[k] ??= []).push(x);
    return acc;
  }, {});
}

function median(vals: number[]): number {
  if (!vals.length) return 0;
  const s = [...vals].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 !== 0 ? s[m] : (s[m - 1] + s[m]) / 2;
}

export function timeToSecs(t: string): number {
  const p = t.split(':').map(Number);
  return (p[0] ?? 0) * 3600 + (p[1] ?? 0) * 60 + (p[2] ?? 0);
}

/**
 * Seconds elapsed from time string `a` to time string `b`.
 * Returns a negative value if b < a (caller should treat as invalid / no cycle).
 */
export function timeDiffSeconds(a: string, b: string): number {
  return timeToSecs(b) - timeToSecs(a);
}

/** Earliest row first — parses date + time with Moment (handles unpadded hours, multiple date shapes). */
export function compareCoatingRowsChronological(a: CoatingRow, b: CoatingRow): number {
  return rowChronologicalSortValue(a) - rowChronologicalSortValue(b);
}

/**
 * Pump-house session drilldown: keep rows from the chronologically first **joint** row
 * whose joint id parses to integer **1** (same digit rule as FPY), plus every row at or
 * after that moment (joints and events). Omits preamble joints (e.g. high test numbers)
 * before production joint 1. If no joint `1` exists, returns `rows` unchanged.
 */
export function sliceRowsFromFirstJointOne(rows: CoatingRow[]): CoatingRow[] {
  if (!rows.length) return rows;
  const sorted = [...rows].sort(compareCoatingRowsChronological);
  const anchor = sorted.find(
    (r) =>
      !r.isEvent &&
      /^\d+$/.test(String(r.joint ?? '').trim()) &&
      parseInt(String(r.joint).trim(), 10) === 1,
  );
  if (!anchor) return rows;
  return rows.filter((r) => compareCoatingRowsChronological(anchor, r) <= 0);
}

/**
 * Non-event rows only: same session keys as {@link buildSessionSummaries} (`date||user`),
 * each group trimmed with {@link sliceRowsFromFirstJointOne}, then concatenated.
 * Used for pipe-level first-pass / total yield and joints sprayed on the summary page.
 */
export function jointRowsFromFirstJointOnePerSession(rows: CoatingRow[]): CoatingRow[] {
  const jointOnly = rows.filter((r) => !r.isEvent);
  if (!jointOnly.length) return [];
  const grouped = groupBy(jointOnly, (r) => `${r.date}||${r.user}`);
  return Object.values(grouped).flatMap((grp) => sliceRowsFromFirstJointOne(grp));
}

function compareRowDateTime(a: CoatingRow, b: CoatingRow): number {
  return compareCoatingRowsChronological(a, b);
}

/**
 * Normalizes CSV/Excel date to DD-MM-YYYY for stable summary FPY keys
 * (same calendar day must map to one string even if source uses 1/2/2026 vs 01.02.2026).
 */
function normalizeDateDmy(raw: string): string {
  const m = parseCoatingDateOnly(raw);
  if (m.isValid()) return m.format('DD-MM-YYYY');
  const t = raw.trim();
  return t || 'unknown-date';
}

/** UTC seconds at midnight for a coating `date` cell (DD.MM.YYYY, YYYY-MM-DD, …). */
export function coatingDateRawToUtcMidnight(raw: string): number {
  const m = moment.utc((raw ?? '').trim(), [...COATING_DATE_FORMATS], true).startOf('day');
  return m.isValid() ? m.valueOf() / 1000 : 0;
}

/** Ascending calendar order for raw date strings (avoids lexicographic bugs on DD.MM.YYYY). */
export function compareCoatingDateStringsAsc(a: string, b: string): number {
  return coatingDateRawToUtcMidnight(a) - coatingDateRawToUtcMidnight(b);
}

/** Session summary FPY: one first attempt per (calendar day, joint), not joint alone. */
function fpySummaryGroupKey(r: CoatingRow): string {
  return `${normalizeDateDmy(r.date)}||${r.joint}`;
}

/** Distinct spray targets for counts: same joint on different days are separate (summary / KPIs). */
function distinctSprayTargetKey(r: CoatingRow): string {
  return `${normalizeDateDmy(r.date)}||${String(r.joint ?? '').trim()}`;
}

/** Only numeric joint ids count toward session-summary FPY (avoids blank joint / stray text in the joint column). */
function isFpySummaryJointRow(r: CoatingRow): boolean {
  return /^\d+$/.test(String(r.joint ?? '').trim());
}

// ─── exported formatters ──────────────────────────────────────────────────────

export function fmtDuration(secs: number): string {
  if (secs <= 0) return '—';
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return m === 0 ? `${s}s` : `${m}m ${String(s).padStart(2, '0')}s`;
}

export function fmtHoursMinutes(mins: number): string {
  if (mins <= 0) return '0m';
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return h === 0 ? `${m}m` : `${h}h ${String(m).padStart(2, '0')}m`;
}

export function fmtDateShort(raw: string): string {
  const m = parseCoatingDateOnly(raw);
  if (!m.isValid()) return raw;
  return `${m.date()} ${MONTH_SHORT[m.month()] ?? String(m.month() + 1)}`;
}

/** Formats a date string as DD-MMM-YYYY, e.g. "13-Apr-2026". */
export function fmtDateFull(raw: string): string {
  const m = parseCoatingDateOnly(raw);
  if (!m.isValid()) return raw;
  const mon = MONTH_SHORT[m.month()] ?? String(m.month() + 1);
  return `${String(m.date()).padStart(2, '0')}-${mon}-${m.year()}`;
}

// ─── sensor-fault detection ───────────────────────────────────────────────────

/**
 * Sensor dropout / invalid water reading — excluded from FPY numerator (still in denominator).
 * Treats missing, non-numeric, and 0 the same as a 0 reading (parseInt('') is NaN and was wrongly treated as OK).
 */
export function isSensorFault(r: CoatingRow): boolean {
  const s = String(r.waterTemp ?? '').trim();
  if (!s) return true;
  const n = parseFloat(s);
  return !Number.isFinite(n) || n === 0;
}

// ─── pass-condition helper ────────────────────────────────────────────────────

/**
 * First-pass for yield: all four must match exactly — any other value fails.
 * (Downstroke/upstroke stall Pass; flush Flush OK; prime Prime OK.)
 */
export function isFirstPass(r: CoatingRow): boolean {
  return (
    r.downstrokeStall === 'Pass' &&
    r.upstrokeStall   === 'Pass' &&
    r.systemFlushed   === 'Flush OK' &&
    r.systemPrimed    === 'Prime OK'
  );
}

/**
 * Fault buckets for the spray overview "Top fault reasons" — shared with session drilldown totals.
 * Joint row: each matching condition increments separately (both stalls, flush, and prime can all fire).
 * Event row: one increment per non-empty trimmed {@link CoatingRow.eventMessage}.
 */
export function buildFaultReasonCounts(jointOnly: CoatingRow[], events: CoatingRow[]): Record<string, number> {
  const faultCounts: Record<string, number> = {};
  const inc = (raw: string) => {
    const key = raw.trim();
    if (!key) return;
    faultCounts[key] = (faultCounts[key] ?? 0) + 1;
  };
  for (const r of jointOnly) {
    if (r.upstrokeStall   === 'Fail')                            inc('Upstroke stall fail');
    if (r.downstrokeStall === 'Fail')                            inc('Downstroke stall fail');
    if (r.systemFlushed && r.systemFlushed !== 'Flush OK')       inc('Flush not OK');
    if (r.systemPrimed  && r.systemPrimed  !== 'Prime OK')       inc('Prime not OK');
  }
  for (const r of events) {
    const msg = r.eventMessage?.trim();
    if (msg) inc(msg);
  }
  return faultCounts;
}

// ─── main computation ─────────────────────────────────────────────────────────

export function computeDashData(
  rows: CoatingRow[],
  limits: TemperatureLimits,
  limitsActive: boolean,
): DashData {
  const jointOnly = rows.filter(r => !r.isEvent);
  const events    = rows.filter(r => r.isEvent);
  /** Per (date, user) session: data from first chronological joint #1 onward — matches drilldown / session table. */
  const jointOnlyFromJ1 = jointRowsFromFirstJointOnePerSession(rows);

  // Distinct (calendar day, joint) after joint-1 trim — aligns with FPY denominator on summary + sessions table.
  const uniqueJoints = new Set(jointOnlyFromJ1.map(distinctSprayTargetKey)).size;

  // ── first-pass yield ──────────────────────────────────────────────────────
  // Must match session summaries: first attempt per (calendar day, joint), numeric joints only.
  // Sensor-fault first attempts count in the denominator but not the numerator.
  const fpyRows = jointOnlyFromJ1.filter(isFpySummaryJointRow);
  const byDateJoint = groupBy(fpyRows, fpySummaryGroupKey);
  let passCount = 0;
  let totalPassAny = 0;
  let total     = 0;
  for (const grp of Object.values(byDateJoint)) {
    const sortedGrp = [...grp].sort(compareRowDateTime);
    const first = sortedGrp[0];
    total++;
    if (first && !isSensorFault(first) && isFirstPass(first)) passCount++;
    if (sortedGrp.some(r => !isSensorFault(r) && isFirstPass(r))) totalPassAny++;
  }
  const fpYield = total > 0 ? Math.round((passCount / total) * 1000) / 10 : 0;
  const totalYield = total > 0 ? Math.round((totalPassAny / total) * 1000) / 10 : 0;

  // ── cycle times / downtime ────────────────────────────────────────────────
  const sortedJoints = [...jointOnly].sort(compareRowDateTime);
  const cycles: number[] = [];
  const gaps:   number[] = [];
  for (let i = 1; i < sortedJoints.length; i++) {
    const delta = timeDiffSeconds(sortedJoints[i - 1].time, sortedJoints[i].time);
    if (delta > 0 && delta < GAP_THRESHOLD_SECS) cycles.push(delta);
    else if (delta >= GAP_THRESHOLD_SECS)        gaps.push(delta);
  }
  const medianCycle       = fmtDuration(median(cycles));
  const totalDowntimeSecs = gaps.reduce((s, g) => s + g, 0);
  const downtimeStr       = fmtHoursMinutes(totalDowntimeSecs / 60);
  const gapCount          = gaps.length;

  // ── dew point margin ──────────────────────────────────────────────────────
  const validJoints = jointOnly.filter(r => !isSensorFault(r));
  const dewMargin   = Math.round(
    median(
      validJoints
        .map(r => parseInt(r.ambientTemp) - parseInt(r.dewPoint))
        .filter(n => !isNaN(n)),
    ) * 10,
  ) / 10;

  // ── daily joints for bar chart ────────────────────────────────────────────
  const byDate = groupBy(jointOnly, r => r.date);
  const dailyJoints: DailyJoint[] = Object.entries(byDate)
    .map(([date, jts]) => ({
      date: fmtDateShort(date),
      _raw: date,
      pass: jts.filter(r => r.downstrokeStall === 'Pass' && r.upstrokeStall === 'Pass').length,
      fail: jts.filter(r => r.downstrokeStall === 'Fail'  || r.upstrokeStall === 'Fail').length,
    }))
    .sort((a, b) => compareCoatingDateStringsAsc(a._raw, b._raw))
    .map(({ _raw: _, ...rest }) => rest);

  // ── top fault reasons ─────────────────────────────────────────────────────
  // Same buckets as {@link buildFaultReasonCounts} (joint stall/flush/prime + event_message).

  const faultCounts = buildFaultReasonCounts(jointOnly, events);

  // Full list (sorted by count); UI scrolls — no cap.
  const faults: FaultEntry[] = Object.entries(faultCounts)
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count)
    .map((f, i) => ({ ...f, color: FAULT_COLORS[i % FAULT_COLORS.length] ?? FAULT_COLOR_FALLBACK }));

  // ── environmental ranges ──────────────────────────────────────────────────
  const envRange = (field: keyof CoatingRow) => {
    const vals = validJoints.map(r => parseFloat(r[field] as string)).filter(n => !isNaN(n) && n > 0);
    return vals.length ? { min: Math.min(...vals), max: Math.max(...vals) } : { min: 0, max: 0 };
  };
  const envData: EnvEntry[] = [
    { label: 'Ambient',       ...envRange('ambientTemp'),  unit: '°F' },
    { label: 'Humidity',      ...envRange('humidity'),     unit: '%'  },
    { label: 'Dew point',     ...envRange('dewPoint'),     unit: '°F' },
    { label: 'Base temp',     ...envRange('baseTemp'),     unit: '°F' },
    { label: 'Hardener temp', ...envRange('hardenerTemp'), unit: '°F' },
  ];

  // ── excursions ────────────────────────────────────────────────────────────
  const excursionCounts: ExcursionCounts = limitsActive
    ? countExcursions(rows, limits)
    : { water: 0, base: 0, hard: 0, total: 0 };

  // ── operator leaderboard ──────────────────────────────────────────────────
  const byUser = groupBy(jointOnlyFromJ1, r => r.user);
  const operators: OperatorEntry[] = Object.entries(byUser)
    .map(([name, jts]) => {
      const fpyJts = jts.filter(isFpySummaryJointRow);
      const byDJ = groupBy(fpyJts, fpySummaryGroupKey);
      let opPass = 0, opTotal = 0;
      for (const grp of Object.values(byDJ)) {
        const f = [...grp].sort(compareRowDateTime)[0];
        opTotal++;
        if (!f || isSensorFault(f)) continue;
        if (isFirstPass(f)) opPass++;
      }
      return { name, joints: opTotal, yield: opTotal > 0 ? (opPass / opTotal) * 100 : 0 };
    })
    .sort((a, b) => b.yield - a.yield);

  // ── material info ─────────────────────────────────────────────────────────
  const baseBatches     = new Set(jointOnlyFromJ1.map(r => r.baseBatch).filter(Boolean)).size;
  const hardenerBatches = new Set(jointOnlyFromJ1.map(r => r.hardenerBatch).filter(Boolean)).size;
  const coatingType     = jointOnlyFromJ1[0]?.coatingType || jointOnlyFromJ1[0]?.coatingMaterial || jointOnly[0]?.coatingType || jointOnly[0]?.coatingMaterial || 'Unknown';
  const avgPerBatch     = baseBatches > 0 ? `~${Math.round(uniqueJoints / baseBatches)} avg` : 'Unknown';

  return {
    kpis: { jointsSprayed: uniqueJoints, fpYield, totalYield, medianCycle, downtimeStr, gapCount, dewMargin },
    dailyJoints,
    faults,
    envData,
    operators,
    material: { coatingType, totalJoints: uniqueJoints, baseBatches, hardenerBatches, avgPerBatch },
    excursions: excursionCounts,
  };
}

// ─── session-level drilldown KPIs ────────────────────────────────────────────

export interface SessionDrilldownKpis {
  /** Distinct (normalized calendar day, joint); aligns with summary joints sprayed logic. */
  uniqueJoints: number;
  totalAttempts: number;
  fpYield: number;
  /** Any-attempt pass rate for the same (day, joint) buckets as {@link fpYield}. */
  totalYield: number;
  medianCycleStr: string;
  downtimeStr: string;
  gapCount: number;
  /** Count of non-joint (`isEvent`) rows in this session — raw device log lines, one per row. */
  faultEvents: number;
  eStops: number;
  stallFails: number;
  retryText: string;
}

/**
 * Computes session-level KPIs for the drilldown view.
 * Mirrors the reference SessionDrilldown.tsx calculations but uses CoatingRow (camelCase).
 */
export function computeSessionKpis(rows: CoatingRow[]): SessionDrilldownKpis {
  const jointOnly = rows.filter(r => !r.isEvent);
  const events    = rows.filter(r => r.isEvent);
  const uniqueJoints  = new Set(jointOnly.map(distinctSprayTargetKey)).size;
  const totalAttempts = jointOnly.length;

  // First-pass yield — (calendar day, joint), numeric joints only (matches summary / session table)
  const fpyRows = jointOnly.filter(isFpySummaryJointRow);
  const byDateJoint = groupBy(fpyRows, fpySummaryGroupKey);
  let passCount = 0, totalPassAny = 0, total = 0;
  for (const grp of Object.values(byDateJoint)) {
    const sortedGrp = [...grp].sort(compareRowDateTime);
    const f = sortedGrp[0];
    total++;
    if (f && !isSensorFault(f) && isFirstPass(f)) passCount++;
    if (sortedGrp.some(r => !isSensorFault(r) && isFirstPass(r))) totalPassAny++;
  }
  const fpYield = total > 0 ? Math.round((passCount / total) * 1000) / 10 : 0;
  const totalYield = total > 0 ? Math.round((totalPassAny / total) * 1000) / 10 : 0;

  // Cycle times / downtime
  const sorted = [...jointOnly].sort(compareRowDateTime);
  const cycles:  number[] = [];
  const gapSecs: number[] = [];
  for (let i = 1; i < sorted.length; i++) {
    const delta = timeDiffSeconds(sorted[i - 1].time, sorted[i].time);
    if (delta > 0 && delta < GAP_THRESHOLD_SECS) cycles.push(delta);
    else if (delta >= GAP_THRESHOLD_SECS)        gapSecs.push(delta);
  }

  // Retry summary — multiple rows for same joint id in this slice
  const byJoint = groupBy(jointOnly, r => r.joint);
  const retryJoints = Object.values(byJoint).filter(g => g.length > 1);
  const retryText = retryJoints.length > 0
    ? retryJoints
        .map(g => `j${g[0]!.joint} ×${g.length}`)
        .slice(0, 3)
        .join(', ') + (retryJoints.length > 3 ? '…' : '')
    : 'no retries';

  const stallFails  = jointOnly.filter(r => r.downstrokeStall === 'Fail' || r.upstrokeStall === 'Fail').length;
  const faultEvents = events.length;
  const eStops      = events.filter(r =>
    r.eventMessage?.includes('Emergency') || r.eventMessage?.includes('E-stop'),
  ).length;

  return {
    uniqueJoints,
    totalAttempts,
    fpYield,
    totalYield,
    medianCycleStr: fmtDuration(median(cycles)),
    downtimeStr:    fmtHoursMinutes(gapSecs.reduce((s, g) => s + g, 0) / 60),
    gapCount:       gapSecs.length,
    faultEvents,
    eStops,
    stallFails,
    retryText,
  };
}

// ─── session aggregate table ──────────────────────────────────────────────────

/**
 * Builds one summary row per (date, user) combination — used to populate the
 * clickable sessions aggregate table shown before the user drills into a session.
 */
export function buildSessionSummaries(rows: CoatingRow[]): SessionSummary[] {
  const jointOnly = rows.filter(r => !r.isEvent);
  const grouped   = groupBy(jointOnly, r => `${r.date}||${r.user}`);

  return Object.entries(grouped)
    .map(([key, records]) => {
      const recordsFromJ1 = sliceRowsFromFirstJointOne(records);
      const first = recordsFromJ1[0] ?? records[0]!;
      const [date = '', user = ''] = key.split('||');

      // Distinct (calendar day, joint) vs. total attempts (from first joint #1 onward)
      const joints   = new Set(recordsFromJ1.map(distinctSprayTargetKey)).size;
      const attempts = recordsFromJ1.length;

      // First-pass yield: group by normalized day-month-year + joint (numeric joints only)
      const fpyRecords = recordsFromJ1.filter(isFpySummaryJointRow);
      const byDateJoint = groupBy(fpyRecords, fpySummaryGroupKey);
      let passCount = 0, totalPassAny = 0, total = 0;
      for (const grp of Object.values(byDateJoint)) {
        const sortedGrp = [...grp].sort(compareRowDateTime);
        const f = sortedGrp[0];
        total++;
        if (f && !isSensorFault(f) && isFirstPass(f)) passCount++;
        if (sortedGrp.some(r => !isSensorFault(r) && isFirstPass(r))) totalPassAny++;
      }
      const fpYield = total > 0 ? Math.round((passCount / total) * 1000) / 10 : 0;
      const totalYield = total > 0 ? Math.round((totalPassAny / total) * 1000) / 10 : 0;

      // Median cycle time
      const sorted = [...recordsFromJ1].sort(compareRowDateTime);
      const cycles: number[] = [];
      for (let i = 1; i < sorted.length; i++) {
        const d = timeDiffSeconds(sorted[i - 1].time, sorted[i].time);
        if (d > 0 && d < GAP_THRESHOLD_SECS) cycles.push(d);
      }
      const medianCycle = fmtDuration(median(cycles));

      // Fault count (same conditions as faultCounts in computeDashData)
      let faultCount = 0;
      for (const r of recordsFromJ1) {
        if (isSensorFault(r))                                        faultCount++;
        else if (r.upstrokeStall === 'Fail' || r.downstrokeStall === 'Fail') faultCount++;
        else if ((r.systemFlushed && r.systemFlushed !== 'Flush OK') ||
                 (r.systemPrimed  && r.systemPrimed  !== 'Prime OK')) faultCount++;
      }

      const status: SessionStatus =
        fpYield >= 95 ? 'pass' : fpYield >= 80 ? 'warn' : 'fail';

      // Same session key as `groupBy` — include event rows so we pick `s3Link` from any upload row.
      const sessionRowsAll = rows.filter((r) => `${r.date}||${r.user}` === key);
      const s3Link = sessionRowsAll.find(
        (r) => typeof r.s3Link === 'string' && r.s3Link.trim() !== '',
      )?.s3Link?.trim();

      return {
        key,
        date,
        user: user || 'Unknown',
        coatingType:   first.coatingType  || first.coatingMaterial || '—',
        // Collect all distinct batch numbers used in this session (from joint #1 onward)
        baseBatch:     Array.from(new Set(recordsFromJ1.map(r => r.baseBatch).filter(Boolean))).join(', ') || '—',
        hardenerBatch: Array.from(new Set(recordsFromJ1.map(r => r.hardenerBatch).filter(Boolean))).join(', ') || '—',
        joints,
        attempts,
        fpYield,
        totalYield,
        medianCycle,
        faultCount,
        status,
        s3Link,
      };
    })
    .sort((a, b) => compareCoatingDateStringsAsc(b.date, a.date) || a.user.localeCompare(b.user));
}

/**
 * Total temperature excursions per session key (`date||user`), using the same
 * rules as {@link countExcursions} on joint rows only.
 */
export function buildSessionExcursionTotals(
  rows: CoatingRow[],
  limits: TemperatureLimits,
): Record<string, number> {
  const grouped = groupBy(jointRecords(rows), r => `${r.date}||${r.user}`);
  return Object.fromEntries(
    Object.entries(grouped).map(([key, recs]) => [
      key,
      countExcursions(sliceRowsFromFirstJointOne(recs), limits).total,
    ]),
  );
}
